import React, { useEffect, useState } from "react";
import api, { eur, fmtDate } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Coins, AlertTriangle, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";

const kg = (v) => `${v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`;
const errMsg = (e) => e?.response?.data?.detail || "Une erreur est survenue";
const onlyDigits4 = (v) => v.replace(/\D/g, "").slice(0, 4);

function CodeGate({ mode, onSuccess }) {
  // mode "setup" : aucun code encore defini (premiere utilisation) — mode "unlock" : code deja defini.
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitSetup = async () => {
    setError("");
    if (code.length !== 4) { setError("Le code doit contenir 4 chiffres"); return; }
    if (code !== confirm) { setError("Les deux codes ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/commissions/set-code", { new_code: code });
      toast.success("Code de commission défini");
      onSuccess(code);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const submitUnlock = async () => {
    setError("");
    if (code.length !== 4) { setError("Le code doit contenir 4 chiffres"); return; }
    setBusy(true);
    try {
      await api.get("/commissions", { headers: { "X-Commission-Code": code } });
      onSuccess(code);
    } catch (e) {
      setError(errMsg(e));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const submit = mode === "setup" ? submitSetup : submitUnlock;

  return (
    <div className="flex flex-col items-center gap-4 p-10 text-center" data-testid="commission-gate">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Lock className="h-6 w-6" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Espace protégé</p>
        <h3 className="font-display text-xl font-extrabold">
          {mode === "setup" ? "Définir un code à 4 chiffres" : "Entrez le code à 4 chiffres"}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {mode === "setup"
            ? "Ce code, distinct du code d'accès habituel, protège la commission apporteur d'affaires."
            : "Requis pour accéder à la commission apporteur d'affaires."}
        </p>
      </div>
      <div className="flex flex-col items-center gap-3">
        <Input
          type="password"
          inputMode="numeric"
          maxLength={4}
          autoFocus
          className="h-11 w-32 text-center text-xl tracking-[0.5em]"
          data-testid="commission-code-input"
          value={code}
          onChange={(e) => setCode(onlyDigits4(e.target.value))}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="••••"
        />
        {mode === "setup" && (
          <Input
            type="password"
            inputMode="numeric"
            maxLength={4}
            className="h-11 w-32 text-center text-xl tracking-[0.5em]"
            data-testid="commission-code-confirm"
            value={confirm}
            onChange={(e) => setConfirm(onlyDigits4(e.target.value))}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Confirmer"
          />
        )}
        {error && <p className="text-sm text-destructive" data-testid="commission-gate-error">{error}</p>}
        <Button onClick={submit} disabled={busy || code.length !== 4} data-testid="commission-gate-submit">
          {busy ? "..." : mode === "setup" ? "Définir le code" : "Valider"}
        </Button>
      </div>
    </div>
  );
}

function ChangeCodeForm({ code, onChanged, onCancel }) {
  const [current, setCurrent] = useState(code);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError("");
    if (next.length !== 4) { setError("Le nouveau code doit contenir 4 chiffres"); return; }
    if (next !== confirm) { setError("Les deux codes ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/commissions/set-code", { new_code: next, current_code: current });
      toast.success("Code de commission modifié");
      onChanged(next);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 border-b border-border bg-muted/30 p-4" data-testid="commission-change-code">
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Code actuel</Label>
        <Input type="password" inputMode="numeric" maxLength={4} className="h-9 w-24 text-center tracking-[0.4em]"
               value={current} onChange={(e) => setCurrent(onlyDigits4(e.target.value))} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nouveau code</Label>
        <Input type="password" inputMode="numeric" maxLength={4} className="h-9 w-24 text-center tracking-[0.4em]"
               data-testid="commission-new-code" value={next} onChange={(e) => setNext(onlyDigits4(e.target.value))} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirmer</Label>
        <Input type="password" inputMode="numeric" maxLength={4} className="h-9 w-24 text-center tracking-[0.4em]"
               value={confirm} onChange={(e) => setConfirm(onlyDigits4(e.target.value))} />
      </div>
      <Button size="sm" onClick={submit} disabled={busy} data-testid="commission-change-submit">{busy ? "..." : "Enregistrer"}</Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>Annuler</Button>
      {error && <p className="w-full text-sm text-destructive">{error}</p>}
    </div>
  );
}

export const CommissionManager = () => {
  const [configured, setConfigured] = useState(null); // null = chargement
  const [code, setCode] = useState(null); // code valide en memoire pour cette session (jamais stocke sur le disque)
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [changingCode, setChangingCode] = useState(false);

  useEffect(() => {
    api.get("/commissions/lock-status").then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false));
  }, []);

  const load = (opts = {}) => {
    const activeCode = opts.code !== undefined ? opts.code : code;
    if (!activeCode) return;
    const from = opts.dateFrom !== undefined ? opts.dateFrom : dateFrom;
    const to = opts.dateTo !== undefined ? opts.dateTo : dateTo;
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/commissions", { params, headers: { "X-Commission-Code": activeCode } })
      .then((r) => setData(r.data))
      .catch(() => { setCode(null); toast.error("Session expirée, ressaisissez le code."); }); // ex: code change ailleurs
  };

  const onUnlocked = (validCode) => { setConfigured(true); setCode(validCode); load({ code: validCode }); };
  const onCodeChanged = (newCode) => { setCode(newCode); setChangingCode(false); load({ code: newCode }); };
  const reset = () => { setDateFrom(""); setDateTo(""); load({ dateFrom: "", dateTo: "" }); };

  if (configured === null) return null;

  if (!code) {
    return (
      <Card className="mt-6 overflow-hidden" data-testid="commission-manager">
        <CodeGate mode={configured ? "unlock" : "setup"} onSuccess={onUnlocked} />
      </Card>
    );
  }

  return (
    <Card className="mt-6 overflow-hidden" data-testid="commission-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-primary" />
          <div>
            <h3 className="font-display font-bold">Commission apporteur d'affaires</h3>
            <p className="text-xs text-muted-foreground">
              {data ? `${eur(data.rate_per_kg)} / kg` : "…"} sur les factures réglées. Rapport interne, non visible du client.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Réglées depuis le</Label>
            <Input type="date" className="h-9 w-40" data-testid="commission-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Jusqu'au</Label>
            <Input type="date" className="h-9 w-40" data-testid="commission-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <Button size="sm" onClick={() => load()} data-testid="commission-filter-btn">Filtrer</Button>
          {(dateFrom || dateTo) && <Button size="sm" variant="ghost" onClick={reset}>Réinitialiser</Button>}
          <Button size="sm" variant="outline" onClick={() => setChangingCode((v) => !v)} data-testid="commission-change-code-btn">
            <KeyRound className="mr-1 h-3.5 w-3.5" /> Code
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCode(null)} data-testid="commission-lock-btn">
            <Lock className="mr-1 h-3.5 w-3.5" /> Verrouiller
          </Button>
        </div>
      </div>

      {changingCode && <ChangeCodeForm code={code} onChanged={onCodeChanged} onCancel={() => setChangingCode(false)} />}

      {data && (
        <div className="grid grid-cols-1 gap-4 border-b border-border p-4 sm:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Commission totale</p>
            <p className="tabular font-display text-2xl font-extrabold text-primary" data-testid="commission-total">{eur(data.total_commission)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Poids total facturé</p>
            <p className="tabular font-display text-2xl font-extrabold">{kg(data.total_kg)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Factures réglées</p>
            <p className="tabular font-display text-2xl font-extrabold">{data.count}</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N° facture</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Enseigne</TableHead>
              <TableHead>Réglée le</TableHead>
              <TableHead className="text-right">Montant facture</TableHead>
              <TableHead className="text-right">Poids</TableHead>
              <TableHead className="text-right">Commission</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.invoices || []).map((inv) => (
              <TableRow key={inv.invoice_id} data-testid={`commission-row-${inv.invoice_id}`}>
                <TableCell className="tabular font-medium">{inv.invoice_number}</TableCell>
                <TableCell>{inv.client_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{inv.enseigne || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(inv.paid_at)}</TableCell>
                <TableCell className="tabular text-right">{eur(inv.amount)}</TableCell>
                <TableCell className="tabular text-right">
                  {kg(inv.kg)}
                  {inv.unparsed_items.length > 0 && (
                    <AlertTriangle
                      className="ml-1 inline h-3.5 w-3.5 text-amber-500"
                      title={`Poids non compté (unité sans kg) : ${inv.unparsed_items.join(", ")}`}
                    />
                  )}
                </TableCell>
                <TableCell className="tabular text-right font-semibold text-primary">{eur(inv.commission)}</TableCell>
              </TableRow>
            ))}
            {data && data.invoices.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Aucune facture réglée pour l'instant.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
